import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { getSelectedPlanFromPath, type ResolvedBranchNode } from '@/lib/branchTypes'
import { stripAtxHeading } from '@/lib/stripAtxHeading'
import type { PlaybookBranchOptionProps } from './playbookBranchChoicesTypes'

// Pre-rendered shape every option variant consumes, so each variant focuses on layout, not wiring.
export type PlaybookBranchOptionModel = {
  tooltipContent: ReactNode | null
  targetNode: ResolvedBranchNode | null
  isPrimary: boolean
  inverted: boolean
  handleClick: () => void
  handlePointerEnter: (event: ReactPointerEvent<HTMLElement>) => void
  handlePointerLeave: (event: ReactPointerEvent<HTMLElement>) => void
}

// Resolves an option's target node, hover handlers, and tooltip body once for all variants to share.
export const playbookBranchOptionModel = ({
  option,
  isSelected,
  selectedPath,
  currentPathIndex,
  nodes,
  onSelect,
  onPlanHover,
  onBranchHover,
}: PlaybookBranchOptionProps): PlaybookBranchOptionModel => {
  const hoveredPath = option.targetId
    ? [...selectedPath.slice(0, currentPathIndex + 1), option.targetId]
    : selectedPath
  const hoveredPlan = option.targetId ? getSelectedPlanFromPath(hoveredPath) : null
  const targetNode = option.targetId ? nodes[option.targetId] ?? null : null
  const tooltipContent = targetNode ? (
    <div className="p-3 max-w-[320px]">
      <div className="font-bold">{stripAtxHeading(targetNode.title)}</div>
      {targetNode.summary && (
        <div className="mt-1 leading-relaxed">{targetNode.summary}</div>
      )}
    </div>
  ) : null
  const isPrimary = !!(option.primary || targetNode?.primary)
  const inverted = isSelected
  const handleClick = () => option.targetId && onSelect(option.targetId)
  // Hover preview is mouse-only: a touch tap synthesizes an enter with no
  // matching leave, which left hoveredPlan/hoveredBranchTargetId stuck after
  // selecting a plan (and the HUD graph animating forward as if hovered).
  const handlePointerEnter = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'mouse') return
    onPlanHover?.(hoveredPlan)
    onBranchHover?.(option.targetId ?? null)
  }
  // The LEAVE clears unconditionally — no pointerType guard. WebKit can
  // deliver synthesized boundary events with an empty pointerType; skipping
  // those latched the hover, and a latched hoveredPlan keeps the HUD graph
  // looping that plan's autoplay sweep forever. Clearing is idempotent, so an
  // oddly-typed leave is always safe to honor. (See also
  // useStuckHoverSafetyNet for leaves that never arrive at all.)
  const handlePointerLeave = () => {
    onPlanHover?.(null)
    onBranchHover?.(null)
  }
  return {
    tooltipContent,
    targetNode,
    isPrimary,
    inverted,
    handleClick,
    handlePointerEnter,
    handlePointerLeave,
  }
}
